import _L from "../../../../util/Labels";
import jsPDF from "jspdf";
import { BoletaBase } from "./BoletaBase";
import { descending } from "d3-array";
import { Entidad } from "../../../../data/Entidad";
import { UIUtilViewCalificacion } from "../../Calificacion";
import autoTable, { RowInput, UserOptions } from "jspdf-autotable";
import CTipoEvaluacion = Entidad.CTipoEvaluacion;
import IBoletaConfig = UIUtilViewCalificacion.IBoletaConfigV2;
import ICalificacionInfoAPeriods = UIUtilViewCalificacion.ICalificacionInfoAPeriods;
import IInfoExtraBoleta = UIUtilViewCalificacion.IInfoExtraBoleta;

interface ISubjectsEvals {
    SubjectName: string;
    QualificationsData: (string | number)[][];
}

interface IAsignacionPeriod {
    Eval: string;
    MaxDtEval: string;
    MaxIdEval: number;
}

class BoletaPDFGeneratorV3 extends BoletaBase {
    protected SortQualif(): void { }
    protected DrawHeader(): number { return 0; }

    private CicloEscolarPeriods = [
        { Nombre: _L("boleta_v3.periodo_1") },
        { Nombre: _L("boleta_v3.periodo_2") },
        { Nombre: _L("boleta_v3.periodo_3") },
    ];

    constructor(config: IBoletaConfig) {
        super(config);
    }

    protected GetPDFAuxDivWidth(): number {
        return this.widthArea / 16;
    }

    protected GetLogoWidth(): number {
        const { LogoDimPercentFromWidthPage } = this.GetBasicPDFStyles();
        return this.width / 100 * LogoDimPercentFromWidthPage;
    }

    protected DrawQualificationTbl(evalTypeSubjectsQualifs: ICalificacionInfoAPeriods[], evalType: Entidad.CTipoEvaluacion, evalCfg?: UIUtilViewCalificacion.ItemConfigEval[]): void {
        // const { CicloEscolarPeriods } = this.config;
        const { FontSizeShort, FontSizePlain } = this.GetBasicPDFStyles();
        const TableStylesAux = this.GetBasicPDFTableStyles();
        this.pdfCoordinates.yPos += this.padding;
        const criteriosIndexMap: Map<string, number[]> = new Map();

        autoTable(this.pdf, {
            startY: this.pdfCoordinates.yPos,
            head: [{ "title": _L("boleta_v3.asig_oficiales") }],
            styles: {
                ...TableStylesAux,
                halign: "center",
                valign: "middle"
            },
            theme: "grid",
            margin: this.padding
        })

        this.pdfCoordinates.yPos = (this.pdf as any).lastAutoTable.finalY;

        const columnsHead: Record<string, string> = {};
        const columnStyles: UserOptions["columnStyles"] = {};
        columnsHead["materias"] = "Asignatura";
        const widthSingleHeaderRect = this.GetSinglePeriodRectWidth();

        columnStyles[0] = { cellWidth: this.widthArea - this.GetPeriodsHeadingColsWidth() }
        this.CicloEscolarPeriods.forEach((period, i) => {
            columnsHead[period.Nombre] = period.Nombre;
            columnStyles[i + 1] = { minCellWidth: widthSingleHeaderRect, fontSize: evalType == CTipoEvaluacion.Cualitativa ? FontSizeShort : FontSizePlain }
        })
        columnsHead["average"] = _L("boleta_v3.promedio");
        columnStyles[this.CicloEscolarPeriods.length + 1] = { cellWidth: widthSingleHeaderRect, fontSize: evalType == CTipoEvaluacion.Cualitativa ? FontSizeShort : FontSizePlain };

        const periodsAverages: IAsignacionPeriod[][] = [];

        const subjectsQualifications = new Map<string, ISubjectsEvals>(evalTypeSubjectsQualifs.map((subject, i) => {
            let auxIndex = 0;
            const ArrCriteriosIndex: number[] = [];
            const subjectCriteriaRowData: string[][] = [];
            if (subject.TieneCriterios) {
                subjectCriteriaRowData.push([subject.MateriaNombre, ...this.CicloEscolarPeriods.map(() => ""), ""])
                auxIndex++;
            }

            const criteriosSubjectRowData = subject.Evaluaciones.map(criterioSubject => {
                const { Evaluaciones, Nombre, MaxDtsEvals, MaxIdsEvals } = criterioSubject
                subject.TieneCriterios && ArrCriteriosIndex.push(auxIndex);
                auxIndex++;
                Evaluaciones.forEach((evals, i) => {
                    periodsAverages[i] = periodsAverages[i] || [];
                    periodsAverages[i].push(...evals.map<IAsignacionPeriod>(d => ({ Eval: d == '' ? '-' : d, MaxDtEval: MaxDtsEvals[i], MaxIdEval: MaxIdsEvals[i] })))
                })

                return [
                    Nombre,
                    ...Evaluaciones.flatMap((d) => d[0] == '' ? "-" : d.join("\n")),
                ]
            });

            subjectCriteriaRowData.push(...criteriosSubjectRowData);
            criteriosIndexMap.set(subject.MateriaNombre + i, ArrCriteriosIndex);
            return [subject.MateriaNombre + i, <ISubjectsEvals>{ SubjectName: subject.MateriaNombre, QualificationsData: subjectCriteriaRowData }]
        }))

        periodsAverages.pop();
        periodsAverages.forEach(d => {
            d.sort((a, b) => descending(a.MaxIdEval, b.MaxIdEval));
            d.sort((a, b) => descending(a.MaxDtEval, b.MaxDtEval));
        })

        const periodsAveragesFormatted = periodsAverages.flatMap(d => UIUtilViewCalificacion._GetEvalGral(d.map(e => e.Eval), evalType)[0])
        let gralAverage = UIUtilViewCalificacion._GetEvalGral([...periodsAveragesFormatted].reverse().filter(d => d != '-'), evalType)[0];
        gralAverage == "" && (gralAverage = "-");

        subjectsQualifications.set(_L("boleta_v3.promedio"), <ISubjectsEvals>{
            SubjectName: _L("boleta_v3.promedio"),
            QualificationsData: [[
                _L("boleta_v3.promedio"),
                ...periodsAveragesFormatted,
                gralAverage,
            ]]
        })

        this.pdfCoordinates.yPos = (this.pdf as any).lastAutoTable.finalY;
        subjectsQualifications.forEach((subjectQualifs, key) => {
            autoTable(this.pdf, {
                startY: this.pdfCoordinates.yPos,
                head: [columnsHead],
                styles: {
                    ...TableStylesAux,
                    cellPadding: { vertical: 1, horizontal: 2 }
                },
                tableWidth: this.widthArea,
                bodyStyles: {
                    halign: "center", valign: "middle",
                },
                columnStyles: columnStyles,
                margin: this.padding,
                theme: "grid",
                body: subjectQualifs.QualificationsData,
                showHead: false,
                rowPageBreak: evalType == CTipoEvaluacion.Colores ? "avoid" : "auto",
                didParseCell: (data) => {
                    this.DidParseCellManage(data, evalType);
                    if (data.column.index === 0) {
                        data.cell.styles.halign = "left";
                    }
                    if (data.column.index === 0 && data.row.index === (subjectQualifs.QualificationsData?.length - 1) && data.cell.raw === _L("boleta_v3.promedio")) {
                        data.cell.styles.fontStyle = 'bold';
                    }
                    if (data.column.index === 0 && criteriosIndexMap.get(key)?.includes(data.row.index)) {
                        data.cell.styles.cellPadding = { vertical: 2, left: this.padding, right: 3 }
                    }
                },
                didDrawCell: (data) => {
                    this.DidDrawCellManage(data, evalType);
                }
            })
            this.pdfCoordinates.yPos = (this.pdf as any).lastAutoTable.finalY;
        })
    }

    protected async DrawBoletaContent(): Promise<void> {
        await this.GetLogoImage();
        let logoHeight = this.DrawLogo();
        this.DrawBoletaTitle(logoHeight);
        this.pdfCoordinates.yPos += logoHeight + this.padding;
        this.DrawInfoBoleta();
        this.DrawEscolaridadInfo();
        const escolaridadEnding = (this.pdf as any).lastAutoTable.finalY;
        const periodsEnding = this.DrawPeriodsGuideTable();

        this.pdfCoordinates.yPos = escolaridadEnding > periodsEnding
            ? escolaridadEnding
            : periodsEnding;

        this.DrawQualifications();
        const signsAlternativeStartY = this.pdfCoordinates.yPos;
        this.DrawAbsencesTbl();
        this.DrawSignItems(signsAlternativeStartY);
    }

    protected GetBasicPDFStyles(): { PageFormat: string; PaddingPercentFromWidthPage: number; LogoDimPercentFromWidthPage: number; FontFamily: string; FontSizeTitle: number; FontSizeSubTitle: number; FontSizePlain: number; FontSizeMedium: number; FontSizeShort: number; FontColor: string; TableFillColor: string; TableLinesColor: string; TableLineWidth: number; DrawAuxColor: string; } {
        const basicPdfStylesBase = super.GetBasicPDFStyles();
        return {
            ...basicPdfStylesBase,
            TableLinesColor: "#d3d3d3", FontSizeTitle: 15, FontSizePlain: 10, FontSizeMedium: 9, FontSizeShort: 7
        }
    }

    private DrawBoletaTitle(logoHeight: number) {
        let textDims = this.pdf.getTextDimensions(_L("boleta_v3.historial_academico"));
        this.pdf.text(
            _L("boleta_v3.historial_academico"),
            (this.width - (this.padding + textDims.w)),
            (this.pdfCoordinates.yPos + logoHeight / 2) + (textDims.h / 2)
        );
    }

    private DrawInfoBoleta() {
        const { NombreAlumno, Grado, Fecha, InfoExtra } = this.config;
        let [alumnoTag, gradoTag, fechaTag]: IInfoExtraBoleta[] = [
            { Tag: _L("boleta_v3.alumno"), Val: NombreAlumno },
            { Tag: _L("boleta_v3.grado"), Val: Grado },
            { Tag: _L("boleta_v3.fecha"), Val: Fecha },
        ];

        const infoExtra = [...InfoExtra.map(d => ({ ...d }))];
        let curp = infoExtra.find(d => d.Tag.toUpperCase() == _L("boleta_v3.curp"));
        let arrInfoExtra = infoExtra.filter(d => d.Tag.toUpperCase() != _L("boleta_v3.curp"));
        let arrInfoBoleta: UIUtilViewCalificacion.IInfoExtraBoleta[] = [];
        arrInfoBoleta.push(alumnoTag);
        if (curp) { arrInfoBoleta.push(curp); }
        arrInfoBoleta.push(gradoTag);
        arrInfoBoleta = [...arrInfoBoleta, ...arrInfoExtra];
        arrInfoBoleta.push(fechaTag);
        arrInfoBoleta.map(info => info.Tag = info.Tag + ":");

        let arrInfoBoletaCol1: UIUtilViewCalificacion.IInfoExtraBoleta[] = arrInfoBoleta.slice(0, Math.round(arrInfoBoleta.length / 2));
        let arrInfoBoletaCol2: UIUtilViewCalificacion.IInfoExtraBoleta[] = arrInfoBoleta.slice(Math.round(arrInfoBoleta.length / 2), arrInfoBoleta.length);

        let dataInfoExtra: (string | number)[][] = [];
        arrInfoBoletaCol1.forEach((d, i) => {
            const col2 = arrInfoBoletaCol2[i] || { Tag: "", Val: "" };
            dataInfoExtra.push([d.Tag, d.Val, col2.Tag, col2.Val])
        })

        let tagWidth = (this.widthArea / 2) / 3;
        let valWidth = (this.widthArea / 2) / 3 * 2;

        const { font, fillColor, textColor } = this.GetBasicPDFTableStyles();
        const { FontSizePlain } = this.GetBasicPDFStyles();

        autoTable(this.pdf, {
            startY: this.pdfCoordinates.yPos,
            head: [{ "col1": "", "col1Val": "", "col2": "", "col2Val": "" }],
            showHead: false,
            body: dataInfoExtra,
            styles: {
                font,
                fillColor,
                textColor,
                fontSize: FontSizePlain,
                halign: "left",
                valign: "middle",
                cellPadding: { vertical: 4, horizontal: 0 },
            },
            columnStyles: {
                0: { cellWidth: tagWidth }, 1: { cellWidth: valWidth },
                2: { cellWidth: tagWidth }, 3: { cellWidth: valWidth }
            },
            theme: "plain",
            margin: this.padding
        })

        this.pdfCoordinates.yPos = (this.pdf as any).lastAutoTable.finalY + this.padding;
    }

    private DrawEscolaridadInfo() {
        const { CicloEscolar, Nivel } = this.config;
        const { FontSizeMedium, FontSizeTitle, FontSizePlain, FontSizeShort } = this.GetBasicPDFStyles();
        const TableStylesAux = this.GetBasicPDFTableStyles();

        let cicloEscText = `${Nivel}\n${_L("boleta_v3.cicloesc")}\n${CicloEscolar}`;
        autoTable(this.pdf, {
            startY: this.pdfCoordinates.yPos,
            tableWidth: this.GetEscolaridadWidth(),
            head: [{ "title": cicloEscText }],
            theme: "grid",
            styles: {
                ...TableStylesAux,
                fontSize: FontSizeMedium,
                fontStyle: "normal",
                halign: "center",
                valign: "middle",
                cellPadding: [this.padding, this.padding / 2, this.padding, this.padding / 2]
            },
            margin: this.padding
        })
    }

    private DrawPeriodsGuideTable() {
        const TableStylesAux = this.GetBasicPDFTableStyles();
        const { FontSizeMedium } = this.GetBasicPDFStyles();
        // const { CicloEscolarPeriods } = this.config;

        this.pdf.setFontSize(FontSizeMedium);
        this.pdfCoordinates.xPos += this.GetEscolaridadWidth() + (this.padding * 4);

        const periodsHeadingTags = this.CicloEscolarPeriods.map(d => d.Nombre);
        periodsHeadingTags.push(_L("boleta_v3.promedio"));
        const heightHeadingCol = this.pdf.getTextDimensions([...periodsHeadingTags].sort((a, b) => this.pdf.getTextDimensions(b).w - this.pdf.getTextDimensions(a).w)[0]).w + this.padding;
        const widthSingleHeaderRect = this.GetSinglePeriodRectWidth();

        autoTable(this.pdf, {
            startY: this.pdfCoordinates.yPos,
            head: [{ "Calificacion": _L("boleta_v3.calificacion") }],
            theme: "grid",
            tableWidth: this.GetPeriodsHeadingColsWidth(),
            styles: {
                ...TableStylesAux,
                fontSize: FontSizeMedium,
                fontStyle: "normal",
                halign: "center",
                valign: "middle",
            },
            margin: {
                vertical: this.padding,
                left: this.pdfCoordinates.xPos,
                right: this.padding,
            }
        })

        let startYPeriodsRects = (this.pdf as any).lastAutoTable.finalY;
        let startYPeriodsTexts = startYPeriodsRects + heightHeadingCol;

        for (const periodText of periodsHeadingTags) {
            this.pdf.rect(this.pdfCoordinates.xPos, startYPeriodsRects, widthSingleHeaderRect, heightHeadingCol);
            let textDims = this.pdf.getTextDimensions(periodText) ?? { w: 0, h: 0 };
            let { w: width, h: height } = textDims;
            let posYText = startYPeriodsTexts - (heightHeadingCol - width) / 2;
            let posXText = (this.pdfCoordinates.xPos + height + (widthSingleHeaderRect - height) / 2);
            this.pdf.text(periodText, posXText, posYText, { angle: 90 })
            this.pdfCoordinates.xPos += widthSingleHeaderRect;
        }

        return startYPeriodsRects + heightHeadingCol;
    }

    private DrawAbsencesTbl() {
        this.pdfCoordinates.yPos += this.padding;
        if (this.pdfCoordinates.yPos + 40 > this.height - this.padding) {
            this.AddFixedOrientationPage("portrait");
            this.pdfCoordinates.yPos = this.padding;
        }

        const TableStylesAux = this.GetBasicPDFTableStyles()
        const { FontSizeMedium } = this.GetBasicPDFStyles();

        autoTable(this.pdf, {
            startY: this.pdfCoordinates.yPos,
            tableWidth: this.GetPeriodsAbsencesWidth(),
            head: [{ "title": _L("boleta_v3.inasistencias") }],
            styles: {
                ...TableStylesAux,
                halign: "center",
                valign: "middle",
                fontStyle: "normal",
            },
            theme: "grid",
            margin: {
                left: this.padding
            },
        })
        this.pdfCoordinates.yPos = (this.pdf as any).lastAutoTable.finalY;

        const { InasistenciasAllPeriods } = this.config;
        const HeadTags: RowInput = [...this.CicloEscolarPeriods.map(d => d.Nombre), _L("boleta_v3.total")];
        const absencesBody: RowInput = [...this.CicloEscolarPeriods.map((_, i) => InasistenciasAllPeriods[i]), InasistenciasAllPeriods.reduce((v, c) => v + (c || 0), 0)];

        autoTable(this.pdf, {
            startY: this.pdfCoordinates.yPos,
            tableWidth: this.GetPeriodsAbsencesWidth(),
            head: [HeadTags],
            body: [absencesBody],
            styles: {
                ...TableStylesAux,
                fontStyle: "normal",
                halign: "center",
                valign: "middle",
                cellPadding: { vertical: 1, horizontal: 2 }
            },
            headStyles: {
                fontSize: FontSizeMedium,
            },
            theme: "grid",
            margin: {
                left: this.padding,
            }
        })
        this.pdfCoordinates.yPos = (this.pdf as any).lastAutoTable.finalY;
    }

    private DrawSignItems(alternativeStartY: number) {
        const { IdEscuela } = this.config;

        this.pdfCoordinates.yPos += this.padding * 3;
        let alternativeWidth = this.GetPeriodsAbsencesWidth() - this.padding;

        const signDivWidth = (this.widthArea / 3);
        const realSignDivWidth = signDivWidth * (8 / 9);
        const signTags = [_L("boleta_v3.sello"), _L("boleta_v3.dir_gral"), _L("boleta_v3.tutor_sign")];

        const nameGralDirec = UIUtilViewCalificacion.MapEscuelasDireccionGeneral.get(IdEscuela);
        let nameSplitted: string[] = [];
        let nameGralDirecHeight = 0;

        if (nameGralDirec) {
            nameSplitted = this.pdf.splitTextToSize(nameGralDirec, realSignDivWidth) as string[];
            for (const txt of nameSplitted) {
                nameGralDirecHeight += this.pdf.getTextDimensions(txt).h;
            }
        }

        let relativeHeightSignElements = (nameGralDirecHeight + this.pdf.getTextDimensions("T").h);
        if (this.pdfCoordinates.yPos + relativeHeightSignElements <= (this.height - this.padding)) {
            this.DrawSignItemsBottom(signDivWidth, nameGralDirec);
        } else {
            let endTblInasistencias = (this.pdf as any).lastAutoTable.finalY;
            let startX = this.padding + this.GetPeriodsAbsencesWidth() + this.padding;
            signTags.push(signTags.shift());
            this.DrawSignItemsAside(alternativeStartY, startX, alternativeWidth, endTblInasistencias, nameGralDirec);
        }
    }

    private DrawSignItemsBottom(signDivWidth: number, gralDirName: string) {
        this.pdfCoordinates.xPos = this.padding;
        let textPadding = (this.padding / 8);
        let realSignDivWidth = signDivWidth * (8 / 9);

        const signTags = [_L("boleta_v3.sello"), _L("boleta_v3.dir_gral"), _L("boleta_v3.tutor_sign")];

        // let posEndY = 0;
        for (const [index, signTag] of signTags.entries()) {
            let auxTextPosY = this.pdfCoordinates.yPos;
            let centerDivX = this.pdfCoordinates.xPos + signDivWidth * 0.5;
            let posInitX = centerDivX - realSignDivWidth / 2;
            let posEndX = posInitX + realSignDivWidth;

            this.pdf.line(posInitX, this.pdfCoordinates.yPos, posEndX, this.pdfCoordinates.yPos)

            if (index == 1 && gralDirName) {
                let nameSplitted = this.pdf.splitTextToSize(gralDirName, realSignDivWidth) as string[];
                nameSplitted.forEach(textName => {
                    let dimSignName = this.pdf.getTextDimensions(textName);
                    let textInitX = centerDivX - dimSignName.w / 2;
                    auxTextPosY += dimSignName.h + textPadding;
                    this.pdf.text(textName, textInitX, auxTextPosY);
                })
            }

            let textDims = this.pdf.getTextDimensions(signTag);
            posInitX = centerDivX - textDims.w / 2;
            let textFinishPos = auxTextPosY + textDims.h + textPadding;
            this.pdf.text(signTag, posInitX, textFinishPos);
            // if (textFinishPos > posEndY) posEndY = textFinishPos;
            this.pdfCoordinates.xPos += signDivWidth;
        }
    }

    private DrawSignItemsAside(startY: number, startX: number, width: number, limitBottomY: number, gralDirName: string) {
        const signTags = [_L("boleta_v3.sello"), _L("boleta_v3.dir_gral"), _L("boleta_v3.tutor_sign")];
        signTags.push(signTags.shift());
        const { FontSizeShort, FontSizeMedium } = this.GetBasicPDFStyles();

        let posYSecondRow = startY;
        let auxStartX = startX;

        let heightSpace = limitBottomY - startY;
        let heightSingRow = heightSpace / 2;
        let heightTextSpace = this.pdf.getTextDimensions("T").h;
        let heightBlankSpaceSign = heightSingRow - heightTextSpace;

        let widthItemSignArea = width / 2;
        let widthItemSignLine = widthItemSignArea * (8 / 9);

        for (const [index, signTag] of signTags.entries()) {
            if (index == 2) {
                startY = posYSecondRow;
                auxStartX = startX + widthItemSignArea / 2;
            }

            let centerDivX = auxStartX + widthItemSignArea / 2;
            let posInitX = centerDivX - widthItemSignLine / 2;
            let posEndX = posInitX + widthItemSignLine;
            let posY = startY + heightBlankSpaceSign;
            this.pdf.line(posInitX, posY, posEndX, posY);

            if (index == 0) {
                this.pdf.setFontSize(FontSizeShort)
                let nameSplitted = this.pdf.splitTextToSize(gralDirName, widthItemSignLine) as string[];
                nameSplitted.forEach(textName => {
                    let dimSignName = this.pdf.getTextDimensions(textName);
                    let textInitX = centerDivX - dimSignName.w / 2;
                    posY += dimSignName.h;
                    this.pdf.text(textName, textInitX, posY);
                })
            }

            this.pdf.setFontSize(FontSizeMedium);
            let textDims = this.pdf.getTextDimensions(signTag);
            posInitX = centerDivX - textDims.w / 2;
            let textFinishPos = posY + textDims.h;
            this.pdf.text(signTag, posInitX, textFinishPos);
            if (index == 0) posYSecondRow = textFinishPos;
            auxStartX += widthItemSignArea;
        }
    }

    private GetEscolaridadWidth() {
        // const { CicloEscolarPeriods } = this.config;
        return this.auxDivWidth * (this.CicloEscolarPeriods.length < 6 ? 3.5 : 2);
    }

    private GetPeriodsHeadingColsWidth() {
        return this.widthArea - this.GetEscolaridadWidth() - (this.padding * 4);
    }

    private GetSinglePeriodRectWidth() {
        // const { CicloEscolarPeriods } = this.config;
        return this.GetPeriodsHeadingColsWidth() / (this.CicloEscolarPeriods.length + 1);
    }

    private GetPeriodsAbsencesWidth() {
        return this.auxDivWidth * 8;
    }

    public async GeneratePDF(): Promise<jsPDF> {
        await this.DrawBoletaContent();
        return this.pdf;
    }
}

export async function _BoletaPDFV3(config: IBoletaConfig) {
    const boletaV3 = new BoletaPDFGeneratorV3(config);
    return await boletaV3.GeneratePDF();
}